import { el } from './dom.ts';
import { icon } from './icons.ts';
import type { IconName } from '../../shared/icons.ts';

export interface ConfirmOptions {
  title: string;
  message?: string;
  icon?: IconName;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Action risquée (relâcher un monstre…) : bouton de confirmation en rouge. */
  danger?: boolean;
  /** Action impossible pour l'instant : bouton de confirmation grisé, seule l'annulation reste. */
  disabled?: boolean;
}

/**
 * Demande une confirmation dans une fenêtre du jeu (à la place du `confirm()` du navigateur). Entrée confirme,
 * Échap ou un clic sur le fond annule ; la touche n'atteint pas le jeu ni la fenêtre en dessous.
 */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const cancel = el('button', { className: 'secondary', textContent: options.cancelLabel ?? 'Annuler' });
    const accept = el('button', { className: options.danger ? 'danger' : '', textContent: options.confirmLabel ?? 'Confirmer', disabled: options.disabled ?? false });
    const dialog = el('section', { className: 'modal confirm-dialog' },
      ...(options.icon ? [el('div', { className: 'confirm-icon' }, icon(options.icon))] : []),
      el('h2', { textContent: options.title }),
      ...(options.message ? [el('p', { className: 'muted', textContent: options.message })] : []),
      el('div', { className: 'confirm-actions' }, cancel, accept));
    dialog.setAttribute('role', 'alertdialog');
    dialog.setAttribute('aria-label', options.title);
    const overlay = el('div', { className: 'modal-overlay confirm-overlay' }, dialog);

    const onKey = (event: KeyboardEvent) => {
      event.stopImmediatePropagation();
      if (event.key === 'Escape') finish(false);
      else if (event.key === 'Enter') {
        event.preventDefault();
        finish(!accept.disabled && document.activeElement !== cancel);
      }
    };
    const finish = (confirmed: boolean) => {
      window.removeEventListener('keydown', onKey, true);
      overlay.remove();
      resolve(confirmed);
    };
    cancel.addEventListener('click', () => finish(false));
    accept.addEventListener('click', () => finish(true));
    overlay.addEventListener('click', (event) => { if (event.target === overlay) finish(false); });
    window.addEventListener('keydown', onKey, true);
    document.body.append(overlay);
    (accept.disabled ? cancel : accept).focus();
  });
}
